import { UserRoles, IItemForGetShortDishInfo } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import React from 'react';
import { IIDsShowInComboDishes } from '../../../../../../../../editDishState/editDishContext';
import DraggableSelectedItem from 'components/SelectedItem/DraggableSelectedItem';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import Text from 'components/Text/Text';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import style from './DnDList.module.scss';

interface IDnDList {
	setDishModifications: (value: string[]) => void;
	removeDishModification: (value: string) => void;
	selectedDishModifications: string[];
	fetchedDishModifications: IItemForGetShortDishInfo[];
	IDsShowInComboDishes: IIDsShowInComboDishes;
}

const DnDList: React.FC<IDnDList> = ({
	selectedDishModifications,
	fetchedDishModifications,
	removeDishModification,
	setDishModifications,
	IDsShowInComboDishes,
}) => {
	const hasChangePositionAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const handleDragEnd = (result: DropResult) => {
		if (!selectedDishModifications.length || !result.destination) {
			return null;
		}
		const items = selectedDishModifications;
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);
		setDishModifications(items);
	};

	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId="dishModificationList">
				{(provided) => (
					<div className={style.suggestedDishList} ref={provided.innerRef} {...provided.droppableProps}>
						{selectedDishModifications.map((el, index) => (
							<Draggable key={el} draggableId={el} index={index}>
								{(provided) => {
									const dish = fetchedDishModifications.find((fetched) => fetched.id === el);
									const translationKey = IDsShowInComboDishes[el]
										? 'showInComboLabel'
										: 'hiddenFromComboLabel';

									return (
										<DraggableSelectedItem
											draggableInnerRef={provided.innerRef}
											provided={provided}
											positionNumber={index + 1}
											onDelete={() => removeDishModification(el)}
											hasDragAccess={hasChangePositionAccess}
											label={dish?.name || ''}
											additionalContent={
												<div className={style.additionalContent}>
													<div className={style.showInCombo}>
														<Text
															translationNameSpace="form"
															translationKey={translationKey}
															theme="secondary"
															size="s"
															weight="bold"
														/>
													</div>
													{dish?.externalId && (
														<ChunkOfTextWithCopying
															text={dish.externalId}
															className={style.externalId}
														/>
													)}
												</div>
											}
										/>
									);
								}}
							</Draggable>
						))}
						{provided.placeholder}
					</div>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export default React.memo(DnDList);
